/* Phase 4.2.1.5 — Workflow Run History
 * Tenant-scoped page at /app/workflows. Lists recent workflow instances
 * across all chains with KPIs (window=30d + lifetime), filterable by
 * definition_id, status, and date range. Per-step breakdown table below.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../../components/ui/table";
import { Activity, RefreshCw, CheckCircle2, XCircle, MinusCircle, Clock, DollarSign, ChevronRight } from "lucide-react";
import { api } from "../../lib/api";
import { useT } from "../../lib/i18n";

const STATUS_TONE = {
  succeeded: "bg-emerald-100 text-emerald-900",
  failed: "bg-rose-100 text-rose-900",
  partial: "bg-amber-100 text-amber-900",
  running: "bg-blue-100 text-blue-900",
};
const RANGE_OPTIONS = [
  {key: "7d", days: 7},
  {key: "30d", days: 30},
  {key: "90d", days: 90},
  {key: "all", days: null},
];

function fmtDuration(ms) {
  if (ms == null) return "—";
  if (ms < 1000) return `${ms}ms`;
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(1)}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${Math.round(s - m * 60)}s`;
}
function fmtCost(c) {
  if (c == null) return "—";
  if (c >= 1) return `$${c.toFixed(2)}`;
  return `$${c.toFixed(4)}`;
}
function fmtPct(p) {
  if (p == null) return "—";
  return `${(p * 100).toFixed(0)}%`;
}
function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

function KpiTile({ label, value, sublabel, icon: Icon, tone, testid }) {
  return (
    <Card data-testid={testid}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{label}</span>
          {Icon && <Icon className={`size-4 ${tone || "text-muted-foreground"}`} />}
        </div>
        <div className="mt-2 text-2xl font-semibold tracking-tight" data-testid={`${testid}-value`}>{value}</div>
        {sublabel && <div className="mt-1 text-[11px] text-muted-foreground" data-testid={`${testid}-sublabel`}>{sublabel}</div>}
      </CardContent>
    </Card>
  );
}

export default function WorkflowHistory() {
  const t = useT();
  const [kpis, setKpis] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [defFilter, setDefFilter] = useState("__all__");
  const [statusFilter, setStatusFilter] = useState("__all__");
  const [rangeKey, setRangeKey] = useState("30d");

  const sinceIso = useMemo(() => {
    const opt = RANGE_OPTIONS.find((o) => o.key === rangeKey);
    if (!opt?.days) return null;
    return new Date(Date.now() - opt.days * 86400000).toISOString();
  }, [rangeKey]);

  const reload = async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      if (defFilter && defFilter !== "__all__") q.set("definition_id", defFilter);
      if (statusFilter && statusFilter !== "__all__") q.set("status", statusFilter);
      if (sinceIso) q.set("since", sinceIso);
      q.set("limit", "50");
      const listP = api.get(`/workflows/instances?${q.toString()}`);
      const kpisQ = new URLSearchParams();
      if (sinceIso) kpisQ.set("since", sinceIso);
      const kpisP = api.get(`/workflows/instances/kpis?${kpisQ.toString()}`);
      const [listR, kpisR] = await Promise.all([listP, kpisP]);
      setRows(listR.data.items || []);
      setTotal(listR.data.total || 0);
      setKpis(kpisR.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defFilter, statusFilter, rangeKey]);

  const window30 = kpis?.window || {};
  const definitionOptions = kpis?.distinct_definition_ids || [];
  const perStep = kpis?.per_step || {};
  const stepNames = Object.keys(perStep).sort();

  return (
    <div className="space-y-6" data-testid="workflow-history-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
            <Activity className="size-6" />
            {t("workflows.history.title")}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">{t("workflows.history.subtitle")}</p>
        </div>
        <Button variant="outline" size="sm" onClick={reload}
                data-testid="wf-history-refresh"
                disabled={loading}>
          <RefreshCw className={`size-4 ${loading ? "animate-spin" : ""}`} /> {t("common.refresh")}
        </Button>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="wf-history-kpi-strip">
        <KpiTile label={t("workflows.history.kpi.total_runs")}
                 value={window30.total_runs ?? "—"}
                 sublabel={t("workflows.history.kpi.window_30d")}
                 icon={Activity}
                 testid="wf-kpi-total-runs" />
        <KpiTile label={t("workflows.history.kpi.success_rate")}
                 value={fmtPct(window30.success_rate)}
                 sublabel={`${window30.succeeded ?? 0} / ${window30.total_runs ?? 0}`}
                 icon={CheckCircle2}
                 tone="text-emerald-600"
                 testid="wf-kpi-success-rate" />
        <KpiTile label={t("workflows.history.kpi.p95_latency")}
                 value={fmtDuration(window30.p95_duration_ms)}
                 sublabel={`p50 ${fmtDuration(window30.p50_duration_ms)}`}
                 icon={Clock}
                 tone="text-indigo-600"
                 testid="wf-kpi-p95-latency" />
        <KpiTile label={t("workflows.history.kpi.cost")}
                 value={fmtCost(window30.cost_usd)}
                 sublabel={t("workflows.history.kpi.window_30d")}
                 icon={DollarSign}
                 tone="text-amber-600"
                 testid="wf-kpi-cost" />
      </div>

      {/* Filter bar */}
      <Card data-testid="wf-history-filters">
        <CardContent className="p-4 flex flex-wrap items-center gap-3">
          <Select value={defFilter} onValueChange={setDefFilter}>
            <SelectTrigger className="w-72" data-testid="wf-filter-definition">
              <SelectValue placeholder={t("workflows.history.filter.definition")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">{t("workflows.history.filter.all_definitions")}</SelectItem>
              {definitionOptions.map((k) => (
                <SelectItem key={k} value={k} data-testid={`wf-filter-definition-opt-${k}`}>{k}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="inline-flex items-center gap-1 rounded-lg bg-muted p-1" data-testid="wf-filter-status-group">
            {[{k: "__all__", lbl: t("workflows.history.filter.all")},
              {k: "succeeded", lbl: t("landing_pages.workflow.status.succeeded")},
              {k: "failed", lbl: t("landing_pages.workflow.status.failed")},
              {k: "running", lbl: t("landing_pages.workflow.status.running")}].map((s) => (
                <button key={s.k}
                        onClick={() => setStatusFilter(s.k)}
                        data-testid={`wf-filter-status-${s.k}`}
                        className={`px-3 py-1 text-xs rounded-md transition ${statusFilter === s.k ? "bg-background shadow-sm font-medium" : "text-muted-foreground hover:text-foreground"}`}>
                  {s.lbl}
                </button>
              ))}
          </div>
          <div className="inline-flex items-center gap-1 rounded-lg bg-muted p-1" data-testid="wf-filter-range-group">
            {RANGE_OPTIONS.map((o) => (
              <button key={o.key}
                      onClick={() => setRangeKey(o.key)}
                      data-testid={`wf-filter-range-${o.key}`}
                      className={`px-3 py-1 text-xs rounded-md transition ${rangeKey === o.key ? "bg-background shadow-sm font-medium" : "text-muted-foreground hover:text-foreground"}`}>
                {t(`workflows.history.range.${o.key}`)}
              </button>
            ))}
          </div>
          <div className="ml-auto text-xs text-muted-foreground" data-testid="wf-history-total">
            {total} {t("workflows.history.runs_total")}
          </div>
        </CardContent>
      </Card>

      {/* Run table */}
      <Card data-testid="wf-history-table-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">{t("workflows.history.table.title")}</CardTitle>
        </CardHeader>
        <CardContent>
          {rows.length === 0 ? (
            <div className="text-center text-sm text-muted-foreground py-12"
                 data-testid="wf-history-empty">
              {loading ? t("common.loading") : t("workflows.history.empty")}
            </div>
          ) : (
            <Table data-testid="wf-history-table">
              <TableHeader>
                <TableRow>
                  <TableHead>{t("workflows.history.col.created")}</TableHead>
                  <TableHead>{t("workflows.history.col.definition")}</TableHead>
                  <TableHead>{t("workflows.history.col.status")}</TableHead>
                  <TableHead className="text-right">{t("workflows.history.col.steps")}</TableHead>
                  <TableHead className="text-right">{t("workflows.history.col.duration")}</TableHead>
                  <TableHead className="text-right">{t("workflows.history.col.cost")}</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id} data-testid={`wf-row-${r.id}`}
                            className="cursor-pointer hover:bg-muted/40">
                    <TableCell className="text-xs">{fmtDate(r.created_at)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs">{r.definition_id}</span>
                        <Badge variant="outline" className="font-mono text-[10px]">
                          v{r.definition_version}
                        </Badge>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-mono ${STATUS_TONE[r.status] || "bg-muted text-muted-foreground"}`}
                            data-testid={`wf-row-${r.id}-status`}>
                        {t(`landing_pages.workflow.status.${r.status}`) || r.status}
                      </span>
                    </TableCell>
                    <TableCell className="text-right text-xs">
                      <span className="text-emerald-700">{r.succeeded_count}</span>
                      {r.failed_count > 0 && <span className="text-rose-700"> · {r.failed_count}f</span>}
                      {r.skipped_count > 0 && <span className="text-muted-foreground"> · {r.skipped_count}s</span>}
                      <span className="text-muted-foreground"> / {r.step_count}</span>
                    </TableCell>
                    <TableCell className="text-right text-xs font-mono">{fmtDuration(r.duration_ms)}</TableCell>
                    <TableCell className="text-right text-xs font-mono">{fmtCost(r.cost_usd)}</TableCell>
                    <TableCell className="w-10">
                      <Link to={`/app/workflows/${r.id}`} data-testid={`wf-row-${r.id}-open`}
                            className="inline-flex items-center text-indigo-700 hover:text-indigo-900">
                        <ChevronRight className="size-4" />
                      </Link>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Per-step KPI table */}
      {stepNames.length > 0 && (
        <Card data-testid="wf-history-perstep-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">{t("workflows.history.perstep.title")}</CardTitle>
            <p className="text-xs text-muted-foreground mt-1">{t("workflows.history.perstep.subtitle")}</p>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("workflows.history.perstep.col.step")}</TableHead>
                  <TableHead className="text-right">{t("workflows.history.perstep.col.count")}</TableHead>
                  <TableHead className="text-right">{t("workflows.history.perstep.col.success")}</TableHead>
                  <TableHead className="text-right">{t("workflows.history.perstep.col.p50")}</TableHead>
                  <TableHead className="text-right">{t("workflows.history.perstep.col.p95")}</TableHead>
                  <TableHead className="text-right">{t("workflows.history.perstep.col.mean_cost")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {stepNames.map((n) => {
                  const s = perStep[n];
                  return (
                    <TableRow key={n} data-testid={`wf-perstep-row-${n}`}>
                      <TableCell className="font-mono text-xs">{n}</TableCell>
                      <TableCell className="text-right text-xs">{s.count}</TableCell>
                      <TableCell className="text-right text-xs">
                        <span className={s.success_rate >= 0.9 ? "text-emerald-700" : (s.success_rate >= 0.6 ? "text-amber-700" : "text-rose-700")}>
                          {fmtPct(s.success_rate)}
                        </span>
                      </TableCell>
                      <TableCell className="text-right text-xs font-mono">{fmtDuration(s.p50_latency_ms)}</TableCell>
                      <TableCell className="text-right text-xs font-mono">{fmtDuration(s.p95_latency_ms)}</TableCell>
                      <TableCell className="text-right text-xs font-mono">{fmtCost(s.mean_cost_usd)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

void XCircle; void MinusCircle;
